import type { FC, SVGProps } from 'react';
import classNames from 'classnames';
import type { MetadataLogoName } from './logos';
import { logos } from './logos';
import './MetadataLogo.scss';

/**
 * Props for the MetadataLogo component.
 * Provides the parameters required to render an SVG logo from the logos folder.
 * Extends all standard SVGSVGElement props, and accepts a logo name that matches the filename (without .svg).
 *
 * @property name - Logo name matching the filename in the logos folder (without .svg).
 * @property className - Additional class name(s) for the SVG element.
 * @property ariaLabel - Accessible label for screen readers.
 */
export interface MetadataLogoProps extends SVGProps<SVGSVGElement> {
  /** Logo name matching the filename in the logos folder (without .svg) */
  name: MetadataLogoName;
  className?: string;
  /** Accessible label for the logo */
  ariaLabel?: string;
}

/**
 * MetadataLogo
 * Displays an SVG logo from the logos folder by filename (e.g. audio-description, dolby-vision).
 * Intended for use as metadata items in MetadataGroup.
 * @param name - Logo name matching the filename in the logos folder (without .svg).
 * @param className - Additional class name applied to the logo.
 * @param ariaLabel - Accessible label for the logo.
 * @param rest - Additional SVG props forwarded to the underlying SVG element.
 */
export const MetadataLogo: FC<MetadataLogoProps> = ({ name, className, ariaLabel, ...rest }) => {
  const LogoImage = logos[name];

  if (!LogoImage) {
    return null;
  }

  return (
    <LogoImage
      className={classNames('vds-metadata-logo', className)}
      aria-label={ariaLabel}
      aria-hidden={!ariaLabel}
      {...rest}
    />
  );
};
